import type { Country } from '@sunwebgroup/uds-ui/fields/phone-field'
import type { ParticipantEditFieldConfiguration, ParticipantEditRule } from '../../domain/types'
import type {
  ParticipantEditCountryPatternAPIResponse,
  ParticipantEditFieldConfigurationAPIResponse,
  ParticipantEditPhoneCountryAPIResponse,
  ParticipantEditRulesAPIResponse,
} from '../types'
import { countryData } from '@sunwebgroup/uds-ui/fields/phone-field'

export function participantEditRulesMapper(data: ParticipantEditRulesAPIResponse): ParticipantEditRule[] {
  return (data?.rules ?? []).map(rule => ({
    fieldName: rule.fieldName,
    currentValue: rule.currentValue,
    editionType: rule.editionType,
    allowedValues: rule.allowedValues ?? [],
    minValue: rule.range?.min,
    maxValue: rule.range?.max,
    reasons: (rule.editionTypeReasons ?? []).map(({ reason }) => reason),
    configurations: (rule.configuration ?? []).map(mapConfiguration),
  }))
}

function mapConfiguration(configuration: ParticipantEditFieldConfigurationAPIResponse): ParticipantEditFieldConfiguration {
  return {
    subType: configuration.subType ?? undefined,
    isMandatory: configuration.isMandatory ?? false,
    pattern: configuration.pattern || undefined,
    options: (configuration.values ?? []).map(({ text, value }) => ({ text, value })),
    phoneCountries: mapPhoneCountries(configuration.additionalSettings?.countries ?? []),
    patternsByCountry: mapPatternsByCountry(configuration.additionalSettings?.allCountries ?? []),
  }
}

function mapPatternsByCountry(countries: ParticipantEditCountryPatternAPIResponse[]): Record<string, string> {
  return Object.fromEntries(
    countries
      .filter(({ iso2, pattern }) => isFilledText(iso2) && typeof pattern === 'string')
      .map(({ iso2, pattern }) => [iso2.toLowerCase(), pattern]),
  )
}

function mapPhoneCountries(countries: ParticipantEditPhoneCountryAPIResponse[]): Country[] {
  return countries
    .filter(({ iso2 }) => isFilledText(iso2))
    .map(({ iso2, name }) => {
      const country = countryData.find(({ isoCode }) => isoCode.toLowerCase() === iso2.toLowerCase())
      return country ? { ...country, name } : undefined
    })
    .filter((country): country is Country => country !== undefined)
}

function isFilledText(value: string): boolean {
  return typeof value === 'string' && value.length > 0
}
